import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Icon } from '@/features/common/Icon';
import { initials } from '@/features/common/labels';
import { publicLayout } from '@/i18n/dictionaries/publicLayout';
import { useTranslation } from '@/i18n/useTranslation';
import '@/features/common/layout/profile-menu.css';

/**
 * The signed-in avatar and the menu behind it (MUD Components/header-auth-preview.html).
 *
 * Written three times before this: in AppShell, in FlowShell, and again in Thymeleaf's
 * `layout/header.html` with the open/close behaviour living in site.js. All three agreed on the
 * markup, which is why the CSS could be shared, and each carried its own copy of the parts that
 * are easy to get subtly wrong — Escape returning focus to the toggle, an outside click closing
 * it, `aria-expanded` tracking the real state.
 *
 * `profileHref` is the only genuine difference between the zones: inside the cabinet the
 * identity row goes to /app/profil, from the public site it goes to /app/cabinet. The row itself
 * is the way in — there is deliberately no separate "my account" entry, which is the shape GitHub
 * and Google use for this menu.
 *
 * The cabinet path carries the /app prefix because that is where the route lives
 * (router.tsx: `path: '/app/profil'`). It was written here as a bare `/profil`, which matches no
 * route at all — so the identity row, the only way into the profile from this menu, went nowhere
 * for every signed-in user. Caught by cabinet-navigation.spec.ts, which had it right all along.
 */
export function ProfileMenu({
  fullName,
  roleLabel,
  profileHref,
}: {
  fullName: string;
  roleLabel?: string | null;
  profileHref: string;
}) {
  const { t } = useTranslation(publicLayout);
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const toggleRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!open) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        setOpen(false);
        // Focus goes back to what opened the menu, or a keyboard user is left at the top of the
        // document with no idea where they were.
        toggleRef.current?.focus();
      }
    };
    const onPointerDown = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('keydown', onKeyDown);
    document.addEventListener('mousedown', onPointerDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.removeEventListener('mousedown', onPointerDown);
    };
  }, [open]);

  const submitLogout = () => {
    const token = document.cookie.match(/(?:^|; )XSRF-TOKEN=([^;]*)/)?.[1] ?? '';
    // A real form POST, not fetch: outside mock-gov, POST /logout is SAML single logout, and its
    // response is either a 302 to the MPass SLO endpoint or an auto-submitting form. Both need a
    // genuine navigation; an XHR would swallow the redirect and leave the IdP session alive.
    const form = document.createElement('form');
    form.method = 'POST';
    form.action = '/logout';
    const csrf = document.createElement('input');
    csrf.type = 'hidden';
    csrf.name = '_csrf';
    csrf.value = decodeURIComponent(token);
    form.appendChild(csrf);
    document.body.appendChild(form);
    form.submit();
  };

  const mark = initials(fullName);

  return (
    <div className="header-profile" ref={rootRef}>
      <button
        ref={toggleRef}
        type="button"
        className="profile-toggle"
        aria-haspopup="true"
        aria-expanded={open}
        aria-controls="profile-dropdown"
        aria-label={t('accountAria', { name: fullName })}
        onClick={() => setOpen((v) => !v)}
      >
        <span className="header-avatar" aria-hidden="true">
          {mark}
        </span>
      </button>
      {open && (
        <div className="profile-dropdown" id="profile-dropdown" role="menu">
          <Link className="profile-dropdown__header" to={profileHref} role="menuitem">
            <span className="header-avatar header-avatar--lg" aria-hidden="true">
              {mark}
            </span>
            <div className="profile-dropdown__info">
              <div className="profile-dropdown__name">{fullName}</div>
              {roleLabel && <div className="profile-dropdown__role">{roleLabel}</div>}
            </div>
          </Link>
          <hr className="profile-dropdown__divider" />
          <button
            type="button"
            className="btn btn--primary profile-dropdown__logout"
            role="menuitem"
            onClick={submitLogout}
          >
            <Icon name="logout" className="profile-dropdown__logout-icon" />
            {t('navLogout')}
          </button>
        </div>
      )}
    </div>
  );
}
